import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/******************************************************************************
 * Copyright (c) 2026 Contributors to the Eclipse Foundation.
 *
 * See the NOTICE file(s) distributed with this work for additional
 * information regarding copyright ownership.
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * https://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *****************************************************************************/
import { useMemo } from 'react';
import { Box, Paper, Typography, Alert, Stack, IconButton } from '@mui/material';
import { BarPlot } from '@mui/x-charts/BarChart';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ChartsReferenceLine, ChartsTooltip, ChartsXAxis, ChartsYAxis, ChartsContainer } from '@mui/x-charts';
import { DateTime } from 'luxon';
export const UsageStatsChart = ({ usageStats, dailyP95, customer, startDate, onStartDateChange, embedded = false, compact = false }) => {
    const dayStart = startDate.startOf('day').toMillis() / 1000;
    const dayEnd = startDate.endOf('day').toMillis() / 1000;
    const formatter = Intl.NumberFormat('en', { notation: 'compact' });
    // we have 5min steps
    const step = 5 * 60;
    const tierCapacity = customer?.tier !== undefined ? (customer.tier.capacity * step) / customer.tier.duration : 0;
    const tierRps = customer?.tier !== undefined ? customer.tier.capacity / customer.tier.duration : 0;
    const tierRpsRounded = Math.round(tierRps * 100) / 100;
    const data = useMemo(() => {
        const arr = [];
        for (let idx = dayStart; idx <= dayEnd; idx += step) {
            arr.push({
                windowStart: idx,
                duration: step,
                count: 0
            });
        }
        for (const stat of usageStats) {
            const idx = (stat.windowStart - dayStart) / step;
            if (idx >= 0 && idx < arr.length) {
                arr[idx].count = stat.count;
            }
        }
        return arr;
    }, [usageStats]);
    const maxDataValue = useMemo(() => {
        if (usageStats.length === 0) {
            return 0;
        }
        else {
            return Math.max(...usageStats.map(v => v.count));
        }
    }, [usageStats]);
    const totalRequests = useMemo(() => usageStats.reduce((sum, d) => sum + d.count, 0), [usageStats]);
    const Wrapper = embedded ? Box : Paper;
    return (_jsxs(LocalizationProvider, { dateAdapter: AdapterLuxon, children: [_jsxs(Wrapper, { sx: { p: 2, mb: embedded ? 2 : 3 }, children: [_jsx(Typography, { variant: 'subtitle2', gutterBottom: true, color: 'text.secondary', children: "Filters" }), _jsxs(Stack, { direction: { xs: 'column', sm: 'row' }, spacing: 2, alignItems: 'center', children: [_jsx(DatePicker, { label: 'Start Date', value: startDate, onChange: onStartDateChange, timezone: 'UTC', slotProps: { textField: { size: 'small' }, actionBar: { actions: ['today'] } } }), _jsx(IconButton, { size: 'small', onClick: () => onStartDateChange(startDate.minus({ days: 1 })), children: _jsx(ChevronLeftIcon, {}) }), _jsx(IconButton, { size: 'small', onClick: () => onStartDateChange(startDate.plus({ days: 1 })), children: _jsx(ChevronRightIcon, {}) })] })] }), usageStats.length === 0 ? (_jsx(Alert, { severity: 'info', children: "No usage data available for this customer." })) : (_jsxs(_Fragment, { children: [_jsx(Wrapper, { sx: { p: 2 }, children: _jsxs(ChartsContainer, { series: [
                                {
                                    type: 'bar',
                                    data: data.map(d => d.count),
                                    label: 'Request Count',
                                    color: 'lightgray'
                                }
                            ], height: compact ? 300 : 400, margin: { top: 30 }, xAxis: [
                                {
                                    id: 'date',
                                    data: data.map(value => value.windowStart * 1000),
                                    valueFormatter: value => DateTime.fromMillis(value, { zone: 'UTC' }).toLocaleString(DateTime.TIME_24_SIMPLE),
                                    scaleType: 'band',
                                    height: 50
                                }
                            ], yAxis: [
                                {
                                    id: 'requests',
                                    scaleType: 'linear',
                                    min: 0,
                                    max: Math.max(tierCapacity, maxDataValue) * 1.2,
                                    valueFormatter: value => formatter.format(value),
                                    width: 50
                                }
                            ], children: [_jsx(BarPlot, {}), tierCapacity > 0 && (_jsx(ChartsReferenceLine, { y: tierCapacity, label: `Tier Limit (${tierRpsRounded} rps)`, labelAlign: 'end', lineStyle: {
                                        strokeDasharray: '10 5',
                                        strokeWidth: 2,
                                        stroke: '#a83244'
                                    } })), dailyP95 !== undefined && (_jsx(ChartsReferenceLine, { y: dailyP95, label: `Daily P95 (${dailyP95})`, labelAlign: 'end', lineStyle: {
                                        strokeDasharray: '10 5',
                                        strokeWidth: 2,
                                        stroke: '#A88132FF'
                                    } })), _jsx(ChartsXAxis, { label: 'Time (UTC)', position: 'bottom', axisId: 'date', tickInterval: 'auto', tickLabelInterval: value => {
                                        const d = new Date(value);
                                        return d.getMinutes() === 0 && (!compact || d.getHours() % 3 === 0);
                                    }, tickLabelPlacement: 'middle', tickLabelStyle: {
                                        fontSize: 10
                                    } }), _jsx(ChartsYAxis, { label: 'Requests', position: 'left', axisId: 'requests', tickLabelStyle: { fontSize: 10 } }), _jsx(ChartsTooltip, {})] }) }), _jsx(Box, { mt: 2, children: _jsxs(Typography, { variant: 'body2', color: 'text.secondary', children: ["Total requests in selected range: ", totalRequests.toLocaleString(), usageStats.length > 0 && _jsxs(_Fragment, { children: [" (", usageStats.length, " data points)"] })] }) })] }))] }));
};
//# sourceMappingURL=usage-stats-chart.js.map